import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, forwardRef, memo, useCallback, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import { useMainSideNavItemHoverExpand } from '~/components/v2/components/workspace/main-side-nav/MainSideNavItemHoverExpandContext';

/** Strato footer rail icons are 14px (`size-3.5`); design-icons has no 3.5 token. */
export const MAIN_SIDE_NAV_SMALL_ICON_SIZE = 14;

export type MainSideNavItemButtonProps = ComponentPropsWithoutRef<typeof Primitive.button>;

const attachMainSideNavItemHoverExpandListener = (
  node: HTMLButtonElement,
  expandOnItemHover: (itemId?: string) => void,
) => {
  const handleMouseEnter = () => {
    expandOnItemHover(node.dataset.itemId);
  };

  node.addEventListener('mouseenter', handleMouseEnter);

  return () => {
    node.removeEventListener('mouseenter', handleMouseEnter);
  };
};

export const MainSideNavItemButton = memo(
  forwardRef<HTMLButtonElement, MainSideNavItemButtonProps>(({ className, ...restOfProps }, ref) => {
    const { expandOnItemHover } = useMainSideNavItemHoverExpand();
    const expandOnItemHoverRef = useRef(expandOnItemHover);
    expandOnItemHoverRef.current = expandOnItemHover;
    const detachHoverExpandListenerRef = useRef<(() => void) | null>(null);

    const itemRef = useCallback((node: HTMLButtonElement | null) => {
      detachHoverExpandListenerRef.current?.();
      detachHoverExpandListenerRef.current = null;

      if (!node) {
        return;
      }

      detachHoverExpandListenerRef.current = attachMainSideNavItemHoverExpandListener(node, (itemId) => {
        expandOnItemHoverRef.current(itemId);
      });
    }, []);

    const composedRef = useComposedRefs(ref, itemRef);

    return (
      <Primitive.button
        className={cn(
          // Width/font-weight stay off transition so expand does not wobble popover anchors.
          // Wash is inset 12px (`left-3`/`right-3`): 36px icon square on the rail, pill when expanded.
          'relative z-0 flex h-9 w-[60px] shrink-0 cursor-pointer items-center justify-start overflow-hidden border-none bg-transparent p-0 text-left font-normal ads:text-metadata text-14 text-gray-10 no-underline outline-none transition-colors duration-200 ease-out before:pointer-events-none before:absolute before:inset-y-0 before:right-3 before:left-3 before:-z-10 before:rounded-8 before:opacity-0 before:transition-opacity before:content-[""] ads:hover:text-primary hover:text-gray-12 hover:no-underline ads:hover:before:bg-hover-subtle hover:before:bg-gray-2 hover:before:opacity-100 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset group-has-[[data-state=open]]/main-side-nav:w-full data-[selected=true]:font-medium data-[state=open]:font-medium ads:data-[selected=true]:text-primary ads:data-[state=open]:text-primary data-[selected=true]:text-gray-12 data-[state=open]:text-gray-12 ads:data-[selected=true]:before:bg-subtle ads:data-[state=open]:before:bg-subtle data-[selected=true]:before:bg-gray-3 data-[state=open]:before:bg-gray-3 data-[selected=true]:before:opacity-100 data-[state=open]:before:opacity-100 ads:hover:data-[selected=true]:before:bg-subtle ads:hover:data-[state=open]:before:bg-subtle hover:data-[selected=true]:before:bg-gray-3 hover:data-[state=open]:before:bg-gray-3 group-data-[hover-expand]/main-side-nav:w-full group-data-[pinned]/main-side-nav:w-full',
          className,
        )}
        data-testid="MAIN_SIDE_NAV_ITEM"
        ref={composedRef}
        {...restOfProps}
      />
    );
  }),
);

MainSideNavItemButton.displayName = 'MainSideNavItemButton';
